import React, { useState, useEffect, useCallback, useMemo } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from "recharts";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { API_URI } from "../../helpers";

/*
 * Pantalla de Precios.
 *
 * Existe porque el panel viejo obligaba a entrar país por país para ver un precio: no había
 * forma de comparar, ni de saber en qué dólar estaba cada uno, ni de mirar hacia atrás. Acá
 * están los 32 países en una tabla, con el precio en dólares al lado del local y el histórico
 * de cada uno.
 *
 * DOS COSAS QUE LA PANTALLA TIENE QUE DECIR Y NO SON OBVIAS:
 *
 * 1. En los países cuya moneda PayPal no acepta, el importe que se cobra es
 *    `precio_local / usd_equiv`. O sea que ahí mover la cotización MUEVE EL PRECIO. Van
 *    marcados con el chip «cobra en USD» — sin eso, la columna del dólar parece informativa.
 * 2. El precio del plan 5 años NO sale de la tabla: se deriva del anual. La fila guardada
 *    difiere en decenas de miles. Va marcado como «derivado» para que nadie cotice el número
 *    de la base.
 *
 * Los colores de serie salen de la paleta validada del skill de dataviz (slots 1 y 2), no de
 * ojo. Los de estado (frenada / desvío) son tokens de estado y no se mezclan con los de serie.
 */

// Paleta validada: ΔE normal 33,6 · protan 24,7 contra el surface claro del panel.
const SERIE_1 = "#2a78d6";   // azul  — la medida principal
const SERIE_2 = "#eb6834";   // naranja — el precio de lista ("antes")
const OK = "#1b7f4f";
const ALERTA = "#b25000";
const GRAVE = "#c0392b";
const TINTA = "#2f2f2f";
const TINTA_2 = "#6b6b6b";
const GRIS_TENUE = "#e6e6e6";

const nf = (n, d = 0) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d });
const usd = (n) => (n == null ? "—" : "US$ " + nf(n, 2));
// El redondeo a un decimal produce "-0,0%", que se lee como un movimiento hacia abajo
// cuando en realidad no se movió nada. Por debajo de medio décimo es cero y punto.
const pct = (n) => {
  if (n == null) return "—";
  if (Math.abs(n) < 0.05) return "0,0%";
  return (n > 0 ? "+" : "") + nf(n, 1) + "%";
};
const fecha = (s) => (s ? String(s).substring(0, 10) : "—");

/** Chip chiquito de estado. Lleva SIEMPRE texto: el color solo no puede ser la información. */
function Chip({ texto, color, titulo }) {
  const c = (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 10, fontSize: 11,
      fontWeight: 600, color: "#fff", background: color, whiteSpace: "nowrap",
    }}>{texto}</span>
  );
  return titulo ? <Tooltip title={titulo}><span>{c}</span></Tooltip> : c;
}

/*
 * Los separadores van por CSS y no en el `style` de cada tile porque tienen que cambiar de
 * lado al apilarse: en el celular los tiles quedan uno abajo del otro y una línea a la
 * derecha de cada uno se lee como un borde suelto. Con estilo en línea no hay media query.
 */
const CSS_TILES = `
.tcdg-tiles { display: flex; flex-wrap: wrap; }
.tcdg-tile { border-right: 1px solid ${GRIS_TENUE}; }
.tcdg-tile:last-child { border-right: none; }
@media (max-width: 720px) {
  .tcdg-tile { border-right: none; border-bottom: 1px solid ${GRIS_TENUE}; }
  .tcdg-tile:last-child { border-bottom: none; }
}`;

/** Número grande + rótulo. Cuando la historia es un número, un número es el gráfico. */
function Tile({ valor, rotulo, color, nota }) {
  return (
    <div className="tcdg-tile" style={{ padding: "14px 18px", flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 26, fontWeight: 300, color: color || TINTA, lineHeight: 1.1 }}>{valor}</div>
      <div style={{ fontSize: 12, color: TINTA_2, marginTop: 3 }}>{rotulo}</div>
      {nota ? <div style={{ fontSize: 11, color: TINTA_2, opacity: 0.8 }}>{nota}</div> : null}
    </div>
  );
}

const ejeY = { tick: { fontSize: 11, fill: TINTA_2 }, axisLine: false, tickLine: false, width: 62 };
const ejeX = { tick: { fontSize: 11, fill: TINTA_2 }, axisLine: false, tickLine: false };

/**
 * Una serie en el tiempo. Un gráfico por medida — NUNCA dos escalas en el mismo eje: el precio
 * local y el de dólares no son comparables y superponerlos inventa una correlación.
 * Con menos de 3 puntos no se dibuja: una línea de un punto no es una serie, es un adorno.
 */
function Serie({ datos, clave, titulo, color, formato, segunda, rotuloSegunda }) {
  if (!datos || datos.length < 3) {
    return (
      <div style={{ padding: "26px 10px", color: TINTA_2, fontSize: 13, textAlign: "center" }}>
        <b>{titulo}</b><br />
        {datos && datos.length ? `Todavía hay ${datos.length} punto${datos.length > 1 ? "s" : ""} de histórico.` : "Sin histórico."}
        {" "}La serie se dibuja cuando haya tres.
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: TINTA, marginBottom: 6 }}>{titulo}</div>
      <ResponsiveContainer width="100%" height={190}>
        <LineChart data={datos} margin={{ top: 6, right: 12, bottom: 2, left: 0 }}>
          <CartesianGrid stroke={GRIS_TENUE} strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="fechaCorta" {...ejeX} />
          <YAxis {...ejeY} tickFormatter={formato} domain={["auto", "auto"]} />
          <RTooltip
            formatter={(v, n) => [formato(v), n]}
            labelStyle={{ color: TINTA, fontSize: 12 }}
            contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid " + GRIS_TENUE }}
          />
          {segunda ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          <Line type="stepAfter" dataKey={clave} name={titulo} stroke={color} strokeWidth={2}
                dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
          {segunda ? (
            <Line type="stepAfter" dataKey={segunda} name={rotuloSegunda} stroke={SERIE_2} strokeWidth={2}
                  strokeDasharray="4 3" dot={{ r: 3 }} isAnimationActive={false} />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Precios() {
  const [tab, setTab] = useState(0);
  const [ov, setOv] = useState(null);
  const [rates, setRates] = useState(null);
  const [plan, setPlan] = useState("3 años");
  const [orden, setOrden] = useState({ campo: "ventas12m", dir: "desc" });
  const [sel, setSel] = useState(null);        // {country_id, plan_id, pais, plan}
  const [hist, setHist] = useState(null);
  const [moneda, setMoneda] = useState(null);  // moneda elegida en el tab de cotizaciones

  useEffect(() => {
    fetch(`${API_URI}/admin/pricing/overview`).then((r) => r.json()).then(setOv);
  }, []);

  useEffect(() => {
    if (tab === 1 && !rates) fetch(`${API_URI}/admin/pricing/rates`).then((r) => r.json()).then(setRates);
  }, [tab, rates]);

  const abrirHistorico = useCallback((pais, planId, planNombre) => {
    setSel({ country_id: pais.id, plan_id: planId, pais: pais.name, plan: planNombre });
    setHist(null);
    fetch(`${API_URI}/admin/pricing/history?country_id=${pais.id}&plan_id=${planId}`)
      .then((r) => r.json()).then(setHist);
  }, []);

  // Sólo los planes que algún país tiene cargados: el selector no puede ofrecer planes vacíos.
  const planesConPrecio = useMemo(() => {
    if (!ov) return [];
    const s = new Set();
    ov.paises.forEach((p) => p.planes.forEach((x) => s.add(x.plan)));
    return Array.from(s);
  }, [ov]);

  const filas = useMemo(() => {
    if (!ov) return [];
    const f = ov.paises.map((p) => {
      const pl = p.planes.find((x) => x.plan === plan) || null;
      return { ...p, pl };
    }).filter((p) => p.pl);
    const { campo, dir } = orden;
    const val = (r) => {
      if (campo === "name") return r.name;
      if (campo === "price") return r.pl.price;
      if (campo === "usd") return r.pl.usd || 0;
      if (campo === "desvio") return Math.abs((r.cotizacion && r.cotizacion.desvio) || 0);
      return r[campo] || 0;
    };
    return f.sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === "string" ? x.localeCompare(y) : x - y;
      return dir === "asc" ? c : -c;
    });
  }, [ov, plan, orden]);

  const ordenar = (campo) =>
    setOrden((o) => ({ campo, dir: o.campo === campo && o.dir === "desc" ? "asc" : "desc" }));

  const th = (campo, texto, alinear) => (
    <TableCell align={alinear || "left"} style={{ fontSize: 12, fontWeight: 600, color: TINTA_2, padding: "8px 10px" }}>
      <TableSortLabel active={orden.campo === campo} direction={orden.dir} onClick={() => ordenar(campo)}>
        {texto}
      </TableSortLabel>
    </TableCell>
  );
  const td = { fontSize: 13, padding: "7px 10px", color: TINTA };

  const serieHist = useMemo(() => {
    if (!hist || !hist.serie) return [];
    return hist.serie.map((s) => ({ ...s, fechaCorta: fecha(s.fecha).substring(2) }));
  }, [hist]);

  const serieMoneda = useMemo(() => {
    if (!rates || !moneda || !rates.series[moneda]) return [];
    return rates.series[moneda].map((s) => ({ ...s, fechaCorta: String(s.fecha).substring(5) }));
  }, [rates, moneda]);

  if (!ov) return <div style={{ padding: 40, color: TINTA_2 }}>Cargando precios…</div>;

  const congeladas = ov.paises.filter((p) => p.cotizacion && !p.cotizacion.aplicada).length;
  const enUsd = ov.paises.filter((p) => p.cobra_en_usd).length;
  const ultimoDolar = ov.paises.map((p) => p.usd_actualizado).filter(Boolean).sort().pop();

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardBody style={{ paddingBottom: 0 }}>
            <style>{CSS_TILES}</style>
            <div className="tcdg-tiles" style={{ borderBottom: "1px solid " + GRIS_TENUE }}>
              <Tile valor={ov.paises.length} rotulo="países con precio" />
              <Tile valor={enUsd} rotulo="cobran en dólares" nota="ahí la cotización ES el precio" color={SERIE_1} />
              <Tile valor={congeladas} rotulo={congeladas === 1 ? "cotización frenada" : "cotizaciones frenadas"}
                    color={congeladas ? ALERTA : OK} nota={congeladas ? "esperan confirmación" : "todas al día"} />
              <Tile valor={fecha(ultimoDolar)} rotulo="último ajuste del dólar" nota="automático, 18:20 hs" />
            </div>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary"
                  style={{ marginTop: 4 }}>
              <Tab label="Precios" />
              <Tab label="Cotizaciones" />
            </Tabs>
          </CardBody>
        </Card>
      </GridItem>

      {tab === 0 && (
        <GridItem xs={12}>
          <Card>
            <CardBody>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: TINTA_2 }}>Plan:</span>
                <Select value={plan} onChange={(e) => setPlan(e.target.value)} style={{ fontSize: 14 }}>
                  {planesConPrecio.map((p) => <MenuItem key={p} value={p} style={{ fontSize: 14 }}>{p}</MenuItem>)}
                </Select>
                <span style={{ fontSize: 12, color: TINTA_2, marginLeft: "auto" }}>
                  Tocá una fila para ver el histórico de ese precio.
                </span>
              </div>

              <div style={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {th("name", "País")}
                      {th("usd_equiv", "Cotización", "right")}
                      {th("desvio", "Desvío", "right")}
                      {th("price", "Precio", "right")}
                      {th("usd", "En dólares", "right")}
                      <TableCell align="right" style={{ fontSize: 12, fontWeight: 600, color: TINTA_2 }}>Antes</TableCell>
                      {th("ventas12m", "Ventas 12m", "right")}
                      <TableCell style={{ fontSize: 12, fontWeight: 600, color: TINTA_2 }}>Desde</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filas.map((p) => {
                      const c = p.cotizacion;
                      const frenada = c && !c.aplicada;
                      const desvio = c ? c.desvio : null;
                      const elegida = sel && sel.country_id === p.id && sel.plan_id === p.pl.plan_id;
                      return (
                        <TableRow key={p.id} hover style={{ cursor: "pointer", background: elegida ? "#eef4fc" : undefined }}
                                  onClick={() => abrirHistorico(p, p.pl.plan_id, p.pl.plan)}>
                          <TableCell style={td}>
                            {p.name}{" "}
                            {p.cobra_en_usd ? (
                              <Chip texto="cobra en USD" color={SERIE_1}
                                    titulo="PayPal no acepta esta moneda: el importe real es precio ÷ cotización, así que mover el dólar mueve el precio" />
                            ) : null}
                          </TableCell>
                          <TableCell align="right" style={td}>
                            {nf(p.usd_equiv, p.usd_equiv < 100 ? 4 : 2)}{" "}
                            <span style={{ color: TINTA_2, fontSize: 11 }}>{p.coin_cod}</span>
                          </TableCell>
                          <TableCell align="right" style={td}>
                            {frenada
                              ? <Chip texto={pct(desvio) + " frenada"} color={GRAVE} titulo={c.motivo} />
                              : <span style={{ color: Math.abs(desvio || 0) > 2 ? ALERTA : TINTA_2 }}>{pct(desvio)}</span>}
                          </TableCell>
                          <TableCell align="right" style={{ ...td, fontWeight: 600 }}>
                            {p.coin_symbol} {nf(p.pl.price)}
                            {p.pl.derivado ? <> <Chip texto="derivado" color={TINTA_2}
                              titulo="El precio de 5 años se calcula del anual: la fila guardada en la tabla no se usa" /></> : null}
                          </TableCell>
                          <TableCell align="right" style={td}>{usd(p.pl.usd)}</TableCell>
                          <TableCell align="right" style={{ ...td, color: TINTA_2, textDecoration: p.pl.list_price ? "line-through" : "none" }}>
                            {p.pl.list_price ? nf(p.pl.list_price) : "—"}
                          </TableCell>
                          <TableCell align="right" style={td}>
                            {nf(p.ventas12m)}
                            <div style={{ fontSize: 11, color: TINTA_2 }}>{usd(p.usd12m)}</div>
                          </TableCell>
                          <TableCell style={{ ...td, color: TINTA_2, fontSize: 12 }}>
                            {fecha(p.pl.desde)}
                            {p.pl.cambios ? <div style={{ fontSize: 11 }}>{p.pl.cambios} cambio{p.pl.cambios > 1 ? "s" : ""}</div> : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      )}

      {tab === 0 && sel && (
        <GridItem xs={12}>
          <Card>
            <CardBody>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: 17, color: TINTA }}>{sel.pais} · {sel.plan}</h4>
                <span style={{ fontSize: 12, color: TINTA_2 }}>histórico del precio</span>
                <span style={{ marginLeft: "auto", fontSize: 12, color: TINTA_2, cursor: "pointer" }}
                      onClick={() => setSel(null)}>cerrar ✕</span>
              </div>

              {!hist ? <div style={{ color: TINTA_2, fontSize: 13 }}>Cargando…</div> : (
                <>
                  {/* Dos gráficos y no dos ejes: el precio local y el de dólares tienen escalas
                      distintas y superponerlos inventaría una relación que no existe. */}
                  <GridContainer>
                    <GridItem xs={12} md={6}>
                      <Serie datos={serieHist} clave="price" segunda="list" rotuloSegunda="Precio de lista"
                             titulo={`Precio en ${hist.moneda}`} color={SERIE_1} formato={(v) => nf(v)} />
                    </GridItem>
                    <GridItem xs={12} md={6}>
                      <Serie datos={serieHist} clave="usd" titulo="El mismo precio, en dólares"
                             color={SERIE_1} formato={(v) => nf(v, 0)} />
                    </GridItem>
                  </GridContainer>

                  <Table size="small" style={{ marginTop: 8 }}>
                    <TableHead>
                      <TableRow>
                        {["Cuándo", "Precio", "Lista", "Dólar del día", "En dólares", "Qué pasó"].map((h, i) => (
                          <TableCell key={h} align={i > 0 && i < 5 ? "right" : "left"}
                                     style={{ fontSize: 12, fontWeight: 600, color: TINTA_2, padding: "6px 10px" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serieHist.slice().reverse().map((s, i) => (
                        <TableRow key={i}>
                          <TableCell style={td}>{String(s.fecha).substring(0, 16)}</TableCell>
                          <TableCell align="right" style={{ ...td, fontWeight: 600 }}>{nf(s.price)}</TableCell>
                          <TableCell align="right" style={{ ...td, color: TINTA_2 }}>{s.list ? nf(s.list) : "—"}</TableCell>
                          <TableCell align="right" style={{ ...td, color: TINTA_2 }}>{nf(s.usd_equiv, 2)}</TableCell>
                          <TableCell align="right" style={td}>{usd(s.usd)}</TableCell>
                          <TableCell style={{ ...td, fontSize: 12, color: TINTA_2 }}>
                            {s.accion === "baseline" ? "estado inicial registrado" : s.accion}
                            {" · "}{s.origen === "sql" ? "por fuera del panel" : s.origen}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      )}

      {tab === 1 && (
        <GridItem xs={12}>
          <Card>
            <CardBody>
              {!rates ? <div style={{ color: TINTA_2 }}>Cargando cotizaciones…</div> : (
                <>
                  <div style={{ fontSize: 12, color: TINTA_2, marginBottom: 8 }}>
                    Se actualizan solas todos los días a las 18:20. Argentina va con el dólar MEP; el resto,
                    con el feed internacional. Una cotización que salta más de 25% se registra pero no se
                    aplica: espera confirmación de una persona.
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["Moneda", "Se cobra con", "Última cotización", "Desvío", "Estado", "Países"].map((h, i) => (
                            <TableCell key={h} align={i >= 2 && i <= 3 ? "right" : "left"}
                                       style={{ fontSize: 12, fontWeight: 600, color: TINTA_2 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rates.monedas.map((m) => {
                          const u = m.ultima || {};
                          const frenada = u.aplicada === false;
                          return (
                            <TableRow key={m.cod} hover style={{ cursor: "pointer", background: moneda === m.cod ? "#eef4fc" : undefined }}
                                      onClick={() => setMoneda(m.cod)}>
                              <TableCell style={{ ...td, fontWeight: 600 }}>
                                {m.cod}{" "}
                                {m.cobra_en_usd ? <Chip texto="cobra en USD" color={SERIE_1}
                                  titulo={"Mover esta cotización mueve el precio en: " + (m.paises_en_usd || "—") +
                                          ". PayPal no acepta la moneda, así que ahí cobra precio ÷ cotización."} /> : null}
                              </TableCell>
                              <TableCell style={td}>{nf(m.usd_equiv, m.usd_equiv < 100 ? 4 : 2)}</TableCell>
                              <TableCell align="right" style={td}>{u.rate != null ? nf(u.rate, u.rate < 100 ? 4 : 2) : "—"}</TableCell>
                              <TableCell align="right" style={{ ...td, color: Math.abs(m.desvio || 0) > 2 ? ALERTA : TINTA_2 }}>
                                {pct(m.desvio)}
                              </TableCell>
                              <TableCell style={td}>
                                {frenada
                                  ? <Chip texto="frenada" color={GRAVE} titulo={u.motivo} />
                                  : <Chip texto="al día" color={OK} />}
                              </TableCell>
                              <TableCell style={{ ...td, color: TINTA_2, fontSize: 12 }}>{m.paises}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {moneda && (
                    <div style={{ marginTop: 16, borderTop: "1px solid " + GRIS_TENUE, paddingTop: 12 }}>
                      <Serie datos={serieMoneda} clave="rate" titulo={`Cuántos ${moneda} vale un dólar`}
                             color={SERIE_1} formato={(v) => nf(v, v < 100 ? 2 : 0)} />
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      )}
    </GridContainer>
  );
}
